import { ObjectId } from "bson";
import {
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { TranslocoModule, TranslocoService } from "@jsverse/transloco";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { AuthService } from "@auth0/auth0-angular";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { combineLatest, Observable } from "rxjs";
import { filter, first } from "rxjs/operators";
import { Store } from "@ngxs/store";
import { ToastrService } from "@sdk/lib/toastr/toastr.service";
import { JournalService } from "@sdk/lib/services/journal.service";
import { TextBlock } from "@sdk/lib/types/block";
import { FREE_TEXT } from "@sdk/lib/values/blocks";
import { AnalyticsService, EVENT_NAMES } from "@uplift-sdk";
import { AddJournalItemEntryAction } from "../../actions/journal-item-entry-list.actions";
import { Group } from "@sdk/lib/types/group";
import { Course, SessionModuleItem } from "@sdk/lib/types/course";
import { Quest } from "@sdk/lib/types/quest.interface";
import { QuestState } from "../../states/quest.state";
import { ProfileState } from "../../states/profile.state";
import { JournalItemEntryState } from "../../states/journal-item-entry.state";
import { PatientProfile } from "@sdk/lib/types/profile";
import {
  hasIndividualCourse,
  recordBelongsToGroup,
} from "@sdk/lib/functions/individual-course";
import { JournalItemEntry } from "@sdk/lib/types/journal-item";
import { QUEST_TYPE_WEBINAR } from "@sdk/lib/values/quest-types";
import {
  ModuleRouteService,
  ModuleRouteType,
} from "@sdk/lib/services/module-route.service";
import { PersonalisedCourseService } from "@sdk/lib/services/personalised-course.service";
import {
  JournalEntrySerializerService,
  SerializedJournalEntry,
} from "@sdk/lib/services/journal-entry-serializer.service";
import { environment } from "../../../environments/environment";
import {
  COMPLETED_QUEST_ROUTE,
  LIFE_PRACTICE_ROUTE,
  QUEST_PATH,
  SHELL_QUEST_PAGE,
  UPDATE_PROFILE,
} from "@sdk/lib/values/shared-paths";
import { GroupState } from "../../states/group-state";
import { AssistantChatbotService } from "./assistant-chatbot.service";
import { PageContext, PageContextService } from "./page-context.service";
import { ModuleLocation, buildModuleLocationIndex } from "./module-location";
import { isCoursePauseEnforced } from "../course-paused.util";
import { ENVIRONMENT_TOKEN } from "@sdk/lib/types/environment.token";
import { trustedResourceUrl } from "@sdk/lib/security/trusted-content";

const CONTENT_LAYOUT_PATH_PREFIXES = [
  `/${QUEST_PATH}`,
  // Webinar pages intentionally excluded — the chatbot should not appear there.
  `/${SHELL_QUEST_PAGE}`,
  `/${LIFE_PRACTICE_ROUTE}`,
  `/${UPDATE_PROFILE}`,
  `/${COMPLETED_QUEST_ROUTE}`,
];

type CatalogSource = "session-list" | "menu-item";

type ModuleStatus = "completed" | "in-progress" | "todo";

interface ModuleCatalogEntry {
  id: string;
  type: ModuleRouteType;
  title: string;
  summary?: string;
  sessionId: string;
  readableId?: string;
  moduleShellId?: string;
  source: CatalogSource;
  status?: ModuleStatus;
  locked?: boolean;
  sessionNumber?: number;
  sessionTitle?: string;
  sessionLabel?: string;
  pageTitle?: string;
}

interface CatalogPatient {
  firstName?: string;
}

interface CatalogMessage {
  type: "mindlife:catalog";
  courseId: string;
  groupId: string;
  modules: ModuleCatalogEntry[];
  patient?: CatalogPatient;
}

interface NavigateMessage {
  type: "mindlife:navigate";
  moduleId: string;
}

interface ReadyMessage {
  type: "mindlife:ready";
}

interface CreateJournalEntryMessage {
  type: "mindlife:create-journal-entry";
  draftId: string;
  text: string;
  private?: boolean;
}

interface JournalEntryCreatedMessage {
  type: "mindlife:journal-entry-created";
  draftId: string;
  entryId: string;
}

interface JournalEntryFailedMessage {
  type: "mindlife:journal-entry-failed";
  draftId: string;
  reason: string;
}

interface JournalMessage {
  type: "mindlife:journal";
  entries: SerializedJournalEntry[];
}

interface PageMessage {
  type: "mindlife:page";
  context: PageContext;
}

type IncomingMessage =
  NavigateMessage | ReadyMessage | CreateJournalEntryMessage;

const MAX_JOURNAL_ENTRIES = 20;

@Component({
  selector: "app-assistant-chatbot",
  templateUrl: "./assistant-chatbot.component.html",
  styleUrls: ["./assistant-chatbot.component.scss"],
  imports: [TranslocoModule],
})
export class AssistantChatbotComponent implements OnInit, OnDestroy {
  private _sanitizer = inject(DomSanitizer);
  private _auth = inject(AuthService);
  private _router = inject(Router);
  private _store = inject(Store);
  private _moduleRoute = inject(ModuleRouteService);
  private _personalisedCourse = inject(PersonalisedCourseService);
  private _destroyRef = inject(DestroyRef);

  chatbotService = inject(AssistantChatbotService);
  private _pageContext = inject(PageContextService);
  private _journalService = inject(JournalService);
  private _toastrService = inject(ToastrService);
  private _transloco = inject(TranslocoService);
  private _analytics = inject(AnalyticsService);
  private _envConfig = inject(ENVIRONMENT_TOKEN, { optional: true });

  readonly draftCoursePreview = !!this._envConfig?.draftCoursePreview;
  readonly isAuthenticated = toSignal(this._auth.isAuthenticated$, { initialValue: false });
  readonly isOpen = toSignal(this.chatbotService.isOpen$, { initialValue: false });

  private _journalSerializer = inject(JournalEntrySerializerService);

  readonly isContentLayout = signal(false);

  readonly iframeRef = viewChild<ElementRef<HTMLIFrameElement>>("chatbotIframe");

  readonly url = input<string>("");
  readonly safeUrl = signal<SafeResourceUrl | null>(null);
  private _chatbotOrigin: string | null = null;
  private _catalog: CatalogMessage | null = null;
  private _lastCatalogSerialized: string | null = null;
  private _journal: JournalMessage | null = null;
  private _lastJournalSerialized: string | null = null;
  private _page: PageMessage | null = null;
  private _lastPageSerialized: string | null = null;
  private _pendingPageSerialized: string | null = null;
  private _iframeReady = false;

  constructor() {
    effect(() => {
      const value = this.url();
      untracked(() => this._applyUrl(value));
    });
  }

  private _applyUrl(value: string): void {
    this._iframeReady = false;
    if (!value) {
      this.safeUrl.set(null);
      this._chatbotOrigin = null;
      return;
    }
    this._chatbotOrigin = this._parseOrigin(value);
    const theme = this._getCurrentTheme();
    const parentOrigin = window.location.origin;
    const parentEnv = environment.production ? "production" : "staging";
    const separator = value.includes("?") ? "&" : "?";
    const params = `theme=${encodeURIComponent(theme)}&parentOrigin=${encodeURIComponent(parentOrigin)}&parentEnv=${encodeURIComponent(parentEnv)}`;
    this.safeUrl.set(trustedResourceUrl(this._sanitizer, `${value}${separator}${params}`) ?? null);
  }

  private _parseOrigin(url: string): string | null {
    try {
      return new URL(url, window.location.origin).origin;
    } catch {
      return null;
    }
  }

  private _getCurrentTheme(): string {
    return localStorage.getItem("sn-dark-mode") === "true" ? "dark" : "light";
  }

  ngOnInit(): void {
    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => this.chatbotService.close());

    this._router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        this._checkContentLayout();
        this._refreshPage();
      });

    this._checkContentLayout();
    this._refreshPage();

    const group$ = this._store.select(GroupState as any) as Observable<Group>;
    const completed$ = this._store.select(
      QuestState.completedQuestList,
    ) as Observable<Quest[]>;
    const drafts$ = this._store.select(
      QuestState.moduleInDraftListAction,
    ) as Observable<Quest[]>;
    const profile$ = this._store.select(
      ProfileState as any,
    ) as Observable<PatientProfile | null>;
    combineLatest([group$, completed$, drafts$, profile$])
      .pipe(
        filter(([group]) => !!group?.course),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(([group, completed, drafts, profile]) => {
        const next = this._buildCatalog(
          group,
          completed ?? [],
          drafts ?? [],
          profile ?? null,
        );
        if (!next) return;
        const serialized = JSON.stringify(next);
        if (serialized === this._lastCatalogSerialized) return;
        this._lastCatalogSerialized = serialized;
        this._catalog = next;
        this._sendCatalog();
      });

    // Re-resolve the page once the state its entity depends on (course /
    // active quest) has loaded — NavigationEnd alone can fire before it's ready.
    combineLatest([group$, this._store.select(QuestState.questBeingEdited)])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._refreshPage());

    const journalEntries$ = this._store.select(
      JournalItemEntryState as any,
    ) as Observable<JournalItemEntry[]>;
    journalEntries$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((entries) => {
        const next = this._buildJournalMessage(entries ?? []);
        const serialized = JSON.stringify(next);
        if (serialized === this._lastJournalSerialized) return;
        this._lastJournalSerialized = serialized;
        this._journal = next;
        this._sendJournal();
      });

    window.addEventListener("message", this._onWindowMessage);
  }

  onIframeLoad(): void {
    if (this._iframeIsStillSameOrigin()) return;
    this._iframeReady = true;
    this._sendCatalog();
    this._sendJournal();
    this._sendPage();
  }

  // Chromium fires `load` for the iframe's about:blank phase too; reading
  // contentWindow.location.href succeeds same-origin and throws cross-origin —
  // the throw is what tells us the iframe is actually at the chatbot URL.
  private _iframeIsStillSameOrigin(): boolean {
    const iframe = this.iframeRef()?.nativeElement;
    if (!iframe || !iframe.contentWindow) return true;
    try {
      const href = iframe.contentWindow.location.href;
      return href === "about:blank" || href === "";
    } catch {
      return false;
    }
  }

  private _sendCatalog(): void {
    if (!this._iframeReady) return;
    const iframe = this.iframeRef()?.nativeElement;
    if (!iframe || !iframe.contentWindow) return;
    if (!this._catalog || !this._chatbotOrigin) return;
    try {
      iframe.contentWindow.postMessage(this._catalog, this._chatbotOrigin);
    } catch {}
  }

  private _sendJournal(): void {
    if (!this._iframeReady) return;
    const iframe = this.iframeRef()?.nativeElement;
    if (!iframe || !iframe.contentWindow) return;
    if (!this._journal || !this._chatbotOrigin) return;
    try {
      iframe.contentWindow.postMessage(this._journal, this._chatbotOrigin);
    } catch {
      // iframe may be mid-navigation; safe to drop — onIframeLoad / ready will resync
    }
  }

  private _sendPage(): void {
    if (!this._iframeReady) return;
    const iframe = this.iframeRef()?.nativeElement;
    if (!iframe || !iframe.contentWindow) return;
    if (!this._page || !this._chatbotOrigin) return;
    try {
      iframe.contentWindow.postMessage(this._page, this._chatbotOrigin);
      this._lastPageSerialized = this._pendingPageSerialized;
    } catch {
      // iframe may be mid-navigation; safe to drop — onIframeLoad / ready will resync
    }
  }

  private _refreshPage(): void {
    this._pageContext.resolveCurrentPage(this._router.url).subscribe((ctx) => {
      const next: PageMessage = { type: "mindlife:page", context: ctx };
      const serialized = JSON.stringify(next);
      if (serialized === this._lastPageSerialized) return;
      this._page = next;
      this._pendingPageSerialized = serialized;
      this._sendPage();
    });
  }

  private _buildJournalMessage(entries: JournalItemEntry[]): JournalMessage {
    const personalPublicEntries = entries
      .filter((e) => e && e.is_patient_generated === true && e.private !== true)
      .slice()
      .sort((a, b) => {
        const da = a.completed_date || a.created_date || "";
        const db = b.completed_date || b.created_date || "";
        return db.localeCompare(da);
      })
      .slice(0, MAX_JOURNAL_ENTRIES);

    const serialized: SerializedJournalEntry[] = [];
    for (const entry of personalPublicEntries) {
      const s = this._journalSerializer.serialize(entry);
      if (s) serialized.push(s);
    }

    return { type: "mindlife:journal", entries: serialized };
  }

  private _buildCatalog(
    group: Group,
    completed: Quest[],
    drafts: Quest[],
    profile: PatientProfile | null,
  ): CatalogMessage | null {
    const baseCourse = group.course as Course | undefined;
    const course =
      profile && baseCourse?.session_list
        ? (this._personalisedCourse.mergePersonalisedCourseState(
            group,
            profile,
          ) ?? baseCourse)
        : baseCourse;
    const courseId = course?._id?.toString();
    const groupId = (group as any)?._id?.toString();
    if (!course || !courseId || !groupId) return null;

    const completedKeys = new Set<string>();
    for (const q of completed ?? []) {
      if (
        q?.readable_id &&
        q?.course_id === courseId &&
        recordBelongsToGroup(q?.group_id, groupId)
      ) {
        completedKeys.add(`${q.session_id}::${q.readable_id}`);
      }
    }
    const draftKeys = new Set<string>();
    for (const q of drafts ?? []) {
      if (
        q?.readable_id &&
        q?.course_id === courseId &&
        recordBelongsToGroup(q?.group_id, groupId)
      ) {
        draftKeys.add(`${q.session_id}::${q.readable_id}`);
      }
    }

    const modules: ModuleCatalogEntry[] = [];
    const seen = new Set<string>();
    const locationIndex = buildModuleLocationIndex(course);

    const ingest = (
      sessions: Array<{
        _id?: any;
        is_unlocked?: boolean;
        session_module_item_list?: Array<SessionModuleItem>;
      }>,
      source: CatalogSource,
    ) => {
      for (const session of sessions ?? []) {
        if (session.is_unlocked === false) continue;
        const sessionId = session._id?.toString();
        if (!sessionId) continue;
        for (const item of session.session_module_item_list ?? []) {
          const entry = this._toCatalogEntry(
            item,
            sessionId,
            source,
            completedKeys,
            draftKeys,
            false,
            locationIndex,
          );
          if (!entry || seen.has(entry.id)) continue;
          seen.add(entry.id);
          modules.push(entry);
        }
      }
    };

    ingest(course.session_list ?? [], "session-list");
    for (const menuItem of course.course_menu_item_list ?? []) {
      ingest(menuItem.menu_item_session_list ?? [], "menu-item");
    }

    const firstName = profile?.first_name?.trim();
    const patient: CatalogPatient | undefined = firstName
      ? { firstName }
      : undefined;

    return {
      type: "mindlife:catalog",
      courseId,
      groupId,
      modules,
      ...(patient ? { patient } : {}),
    };
  }

  private _toCatalogEntry(
    item: SessionModuleItem,
    sessionId: string,
    source: CatalogSource,
    completedKeys: Set<string>,
    draftKeys: Set<string>,
    locked: boolean,
    locationIndex: Map<string, ModuleLocation>,
  ): ModuleCatalogEntry | null {
    const moduleItem = item.module_item as
      | {
          _id?: any;
          title?: string;
          internal_name?: string;
          short_description?: string;
          long_description?: string;
          readable_id?: string;
          type?: string;
          meeting_url?: string;
        }
      | undefined;
    if (!moduleItem) return null;
    const moduleItemId = moduleItem._id?.toString();
    if (!moduleItemId) return null;

    const title = this._resolveTitle(item, moduleItem);
    if (!title) return null;

    const summary = this._resolveSummary(moduleItem);

    const location = this._resolveLocationFields(
      locationIndex.get(moduleItemId),
    );

    const isWebinar =
      item.is_webinar ||
      moduleItem.type === QUEST_TYPE_WEBINAR ||
      Object.prototype.hasOwnProperty.call(moduleItem, "meeting_url");

    // Status is omitted for sources/types without a binary completion model:
    // - menu-item entries (supplementary library, not part of progression)
    // - module-shell modules (journaling shells with recurring entries — no
    //   terminal "done" state)
    const hasCompletionModel =
      source !== "menu-item" && !item.module_shell?._id;
    const status = hasCompletionModel
      ? this._resolveStatus(
          item,
          sessionId,
          moduleItem.readable_id,
          completedKeys,
          draftKeys,
        )
      : undefined;

    if (isWebinar) {
      return {
        id: moduleItemId,
        type: "webinar",
        title,
        summary,
        sessionId,
        source,
        status,
        locked: locked || undefined,
        ...location,
      };
    }

    if (item.is_custom_life_practice) {
      return {
        id: moduleItemId,
        type: "custom-life-practice",
        title,
        summary,
        sessionId,
        readableId: moduleItem.readable_id,
        source,
        status,
        locked: locked || undefined,
        ...location,
      };
    }

    if (item.module_shell?._id) {
      return {
        id: moduleItemId,
        type: "module-shell",
        title,
        summary,
        sessionId,
        moduleShellId: item.module_shell._id.toString(),
        source,
        status,
        locked: locked || undefined,
        ...location,
      };
    }

    return {
      id: moduleItemId,
      type: "quest",
      title,
      summary,
      sessionId,
      readableId: moduleItem.readable_id,
      source,
      status,
      locked: locked || undefined,
      ...location,
    };
  }

  private _resolveLocationFields(
    location: ModuleLocation | undefined,
  ): Partial<
    Pick<
      ModuleCatalogEntry,
      "sessionNumber" | "sessionTitle" | "sessionLabel" | "pageTitle"
    >
  > {
    if (!location) return {};
    if (location.kind === "session") {
      return {
        sessionNumber: location.sessionNumber,
        sessionTitle: location.sessionTitle || undefined,
        sessionLabel: location.sessionLabel,
      };
    }
    return { pageTitle: location.pageTitle || undefined };
  }

  private _resolveStatus(
    item: SessionModuleItem,
    sessionId: string,
    readableId: string | undefined,
    completedKeys: Set<string>,
    draftKeys: Set<string>,
  ): ModuleStatus {
    if (readableId) {
      const key = `${sessionId}::${readableId}`;
      if (completedKeys.has(key)) return "completed";
      if (draftKeys.has(key)) return "in-progress";
    }
    if (item.is_complete) return "completed";
    if (item.last_submitted_module_entry_id) return "in-progress";
    return "todo";
  }

  private _resolveTitle(
    item: SessionModuleItem,
    moduleItem: { title?: string; internal_name?: string },
  ): string | null {
    const candidates = [
      moduleItem.title,
      item.module_shell?.title,
      moduleItem.internal_name,
    ];
    for (const c of candidates) {
      if (typeof c === "string" && c.trim().length > 0) return c.trim();
    }
    return null;
  }

  private _resolveSummary(moduleItem: {
    short_description?: string;
    long_description?: string;
  }): string | undefined {
    const candidates = [
      moduleItem.short_description,
      moduleItem.long_description,
    ];
    for (const c of candidates) {
      if (typeof c === "string" && c.trim().length > 0) return c.trim();
    }
    return undefined;
  }

  private _onWindowMessage = (event: MessageEvent): void => {
    if (!this._chatbotOrigin || event.origin !== this._chatbotOrigin) return;
    const data = event.data as IncomingMessage | null;
    if (!data || typeof data.type !== "string") return;

    if (data.type === "mindlife:ready") {
      this._iframeReady = true;
      this._sendCatalog();
      this._sendJournal();
      this._sendPage();
      return;
    }

    if (data.type === "mindlife:navigate") {
      const moduleId = data.moduleId;
      if (typeof moduleId !== "string" || !moduleId) return;
      this._navigateToModule(moduleId);
      return;
    }

    if (data.type === "mindlife:create-journal-entry") {
      this._handleCreateJournalEntry(data);
    }
  };

  private _handleCreateJournalEntry(msg: CreateJournalEntryMessage): void {
    const text = typeof msg.text === "string" ? msg.text.trim() : "";
    const draftId = typeof msg.draftId === "string" ? msg.draftId : "";
    if (!text || !draftId) return;

    this._auth.user$
      .pipe(
        filter((user) => !!user),
        first(),
      )
      .subscribe((user) => {
        const block: TextBlock = {
          id: new ObjectId().toString(),
          type: FREE_TEXT,
          header: "",
          page: 0,
          position: 0,
          value: text,
        };
        const entry: JournalItemEntry = {
          _id: new ObjectId(),
          creator_user_id: user.sub,
          created_date: new Date().toISOString(),
          block_list: [block],
          journal_item_id: "",
          protocol_week_id: "",
          protocol_id: "",
          group_id: "",
          completed_date: new Date().toISOString(),
          owner_user_id: user.sub,
          is_patient_generated: true,
          private: msg.private === true,
          origin: "chatbot",
        };

        this._journalService.addJournalItemEntry(entry).subscribe({
          next: () => {
            this._store.dispatch(new AddJournalItemEntryAction(entry));
            this._analytics.track(EVENT_NAMES.journal_entry_created, {
              entry_id: entry._id.toString(),
              is_private: !!entry.private,
              content_length: text.length,
              origin: "chatbot",
            });
            this._postToChatbot({
              type: "mindlife:journal-entry-created",
              draftId,
              entryId: entry._id.toString(),
            });
          },
          error: () => {
            this._toastrService.error(
              this._transloco.translate("assistantChatbot.journalSaveFailed"),
            );
            this._postToChatbot({
              type: "mindlife:journal-entry-failed",
              draftId,
              reason: "create-failed",
            });
          },
        });
      });
  }

  private _postToChatbot(
    msg: JournalEntryCreatedMessage | JournalEntryFailedMessage,
  ): void {
    if (!this._iframeReady || !this._chatbotOrigin) return;
    const iframe = this.iframeRef()?.nativeElement;
    if (!iframe || !iframe.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(msg, this._chatbotOrigin);
    } catch {}
  }

  private _navigateToModule(moduleId: string): void {
    const catalog = this._catalog;
    if (!catalog) return;
    const entry = catalog.modules.find((m) => m.id === moduleId);
    if (!entry) return;
    if (entry.type !== "webinar") {
      const group = this._store.selectSnapshot<Group | null>(GroupState as any);
      if (isCoursePauseEnforced(group?.course, this.draftCoursePreview)) {
        this._toastrService.error(
          this._transloco.translate("coursePaused.toast"),
        );
        return;
      }
    }
    this._moduleRoute.navigate({
      type: entry.type,
      moduleItemId: entry.id,
      readableId: entry.readableId,
      moduleShellId: entry.moduleShellId,
      courseId: catalog.courseId,
      groupId: catalog.groupId,
      individual: hasIndividualCourse(
        this._store.selectSnapshot<PatientProfile | null>(ProfileState as any),
      ),
      sessionId: entry.sessionId,
      fromMenuItem: entry.source === "menu-item",
      forceReload: true,
      exitToHome: true,
    });
    this.chatbotService.close();
  }

  private _checkContentLayout(): void {
    const path = this._router.url.split(/[?#]/)[0];
    const isContent = CONTENT_LAYOUT_PATH_PREFIXES.some(
      (prefix) => path === prefix || path.startsWith(prefix + "/"),
    );
    this.isContentLayout.set(isContent);
    document.body.classList.toggle("has-chatbot-fab", isContent);
  }

  toggle(): void {
    this.chatbotService.toggle();
  }

  close(): void {
    this.chatbotService.close();
  }

  ngOnDestroy(): void {
    document.body.classList.remove("has-chatbot-fab");
    window.removeEventListener("message", this._onWindowMessage);
  }
}
